import { useCallback } from "react";
import { useFormatDollarAmount } from "../common/DollarAmount";
import { BarItem, BarItemProps } from "@nivo/bar";
import { animated, to, useSpring } from "@react-spring/web";
import { buildClasses } from "../utils/buildClasses";
import {
  GROUPED_BAR_MOTION,
  GroupedBarChart,
  GroupedBarDatum,
  useGroupedBars,
} from "../common/GroupedBarChart";
import {
  BreakdownSelection,
  BreakdownTooltip,
  UnreviewedNote,
} from "./breakdownChartParts";
import type { BreakdownBarGroup, BreakdownBarRow } from "./breakdownBarRows";
import type { BreakdownType } from "./useCategoryBreakdownData";

// Hex values matching the Tailwind tokens in tailwind.config.js; Nivo needs
// real colors, not class names. Group rows are a shade darker than their
// categories, so the two levels stay apart.
const COLORS: Record<BreakdownType, { group: string; category: string }> = {
  income: { group: "#2AAC6B", category: "#3FD088" }, // green-600, -500
  expenses: { group: "#0284c7", category: "#0ea5e9" }, // blue-600, -500
  savings: { group: "#6930f7", category: "#7d57ff" }, // purple-600, -500
};
const NO_COLOR = "#cbd5e1"; // on-surface-300
const AMOUNT_TEXT_COLOR = "#475569"; // on-surface-600
// Room for the amount at the end of the longest bar.
const MARGIN_RIGHT_PX = 96;

function rowColor(row: BreakdownBarRow): string {
  return row.color ? COLORS[row.color][row.kind] : NO_COLOR;
}

/**
 * Nivo's own bar, plus the row's signed total just past its end. The amount
 * reads the same animated position, width, and fade as the bar, so the two
 * move as one.
 */
function BarWithAmount(props: BarItemProps<GroupedBarDatum>): JSX.Element {
  const { allRowsById, expandedGroupIds } = useGroupedBars<BreakdownBarRow>();
  const formatDollarAmount = useFormatDollarAmount();
  const rowId = String(props.bar.data.indexValue);
  const row = allRowsById.get(rowId);
  const { transform, width, height, labelOpacity } = props.style;
  // An expanded group's total fades out along with its bar; its categories'
  // own totals are what's left to read.
  const { amountOpacity } = useSpring({
    amountOpacity: expandedGroupIds.has(rowId) ? 0 : 1,
    config: GROUPED_BAR_MOTION,
  });
  return (
    <>
      <BarItem {...props} />
      {row && (
        <animated.g
          transform={transform}
          opacity={to([labelOpacity, amountOpacity], (a, b) => a * b)}
          style={{ pointerEvents: "none" }}
        >
          <animated.text
            x={to(width, (w) => Math.max(w, 0) + 6)}
            y={to(height, (h) => h / 2)}
            dominantBaseline="central"
            style={{
              fontFamily: "InterVariable, sans-serif",
              fontSize: 12,
              fill: AMOUNT_TEXT_COLOR,
              fontWeight: row.kind === "group" ? 500 : 400,
            }}
          >
            {formatDollarAmount(row.net, {
              currencyCode: row.currency,
              includeDecimals: true,
              showDollarSign: true,
              showPlus: true,
              showMinus: true,
            })}
          </animated.text>
        </animated.g>
      )}
    </>
  );
}

function BarTooltip({ row }: { row: BreakdownBarRow }): JSX.Element {
  return (
    <BreakdownTooltip
      color={rowColor(row)}
      label={row.label}
      value={row.net}
      signed={true}
      currency={row.currency}
    />
  );
}

/**
 * Every category in category-list order, grouped, with the groups expandable.
 * Each bar's length is the size of its total; the signed total is written at
 * its end.
 *
 * An expanded group's own bar and total go away, since its categories now
 * show the same money in detail. The scale fits the bars that are showing, so
 * it can change as groups expand and collapse.
 */
export function BreakdownBars({
  groups,
  expandedGroupIds,
  onToggleGroup,
  currency,
  unreviewedCount,
  selectedId,
  onSelect,
}: {
  groups: BreakdownBarGroup[];
  expandedGroupIds: ReadonlySet<string>;
  onToggleGroup: (groupId: string) => void;
  currency: string;
  unreviewedCount: number;
  selectedId: string | null;
  onSelect: (selection: BreakdownSelection) => void;
}): JSX.Element {
  const formatDollarAmount = useFormatDollarAmount();

  const getValue = useCallback(
    (row: BreakdownBarRow) =>
      row.kind === "group" && expandedGroupIds.has(row.id)
        ? 0
        : Math.abs(row.net),
    [expandedGroupIds],
  );
  const onSelectRow = useCallback(
    (row: BreakdownBarRow) =>
      onSelect({
        id: row.id,
        label: row.label,
        transactions: row.transactions,
      }),
    [onSelect],
  );

  return (
    <div className={buildClasses("flex", "flex-col")}>
      <GroupedBarChart<BreakdownBarRow>
        groups={groups}
        expandedGroupIds={expandedGroupIds}
        onToggleGroup={onToggleGroup}
        selectedId={selectedId}
        onSelectRow={onSelectRow}
        getValue={getValue}
        getColor={rowColor}
        barContent={BarWithAmount}
        tooltip={BarTooltip}
        formatAxisValue={(value) =>
          formatDollarAmount(value, {
            currencyCode: currency,
            showDollarSign: true,
            showMinus: true,
            compact: true,
          })
        }
        marginRight={MARGIN_RIGHT_PX}
        ariaLabel="Totals by category group and category"
      />
      <UnreviewedNote
        count={unreviewedCount}
        className={buildClasses("pt-3")}
      />
    </div>
  );
}
